import React, {useMemo, useState} from "react";
import {withRouter} from "react-router-dom";
import fileDownload from "js-file-download";
import {useFiltered} from "@atttomyx/shared-hooks";
import {CircularProgress, IconButton, Typography} from "@mui/material";
import {Delete as DeleteIcon, Download as DownloadIcon,} from "@mui/icons-material";
import {Cards, ConfirmDeleteDialog, FiltersAccordion, FloatingAddButton, UserField} from "@atttomyx/react-components";
import {FileCard} from "../cards";
import {AddFileDialog} from "../dialogs";
import {FileFilters} from "../filters";
import {documentService} from "../../services";
import {arrays, strings} from "@atttomyx/shared-utils";
import {progressUtils, sortingUtils} from "../../utils";

const FilesPage = (props) => {
    const { snackbar, dimensions, user, users, documents, files, progress, filters, onSave, onDelete } = props;
    const [ userId, setUserId ] = useState(user.roles.admin ? null : user.id);
    const [ file, setFile ] = useState(null);
    const [ downloading, setDownloading ] = useState(null);
    const [ showAdd, setShowAdd ] = useState(false);
    const [ showDelete, setShowDelete ] = useState(false);
    const [ showFilters, setShowFilters ] = useState(false);

    const filesToUse = useMemo(() => {
        const array = [];

        if (user.roles.admin) {
            const ownedByUser = userId ? documents.filter(document => document.userId === userId) : [];

            arrays.addAll(array, ownedByUser);

        } else {
            const downloaded = documents.filter(document => progressUtils.hasDownloaded(progress, document.id));

            arrays.addAll(array, downloaded);
            arrays.addAll(array, files);
        }

        return array;
    }, [user, documents, files, progress, userId]);

    const filtered = useFiltered(filesToUse, filters, sortingUtils.sortByFileName);

    const downloadFile = (document) => {
        setDownloading(document);

        const success = (bytes) => {
            fileDownload(bytes, document.fileName);
            setDownloading(null);
        };

        const failure = (err) => {
            snackbar.setError(err);
            setDownloading(null);
        };

        documentService.downloadFile(document.id, success, failure);
    };

    return <div className="files-page">
        {user.roles.admin ?
            <div className="field">
                <UserField
                    label="Whose files?"
                    users={users}
                    value={userId}
                    onChange={setUserId}
                    required={true}
                />
            </div> :
            <Typography variant="h5" paragraph={!user.roles.inventor}>
                Files
            </Typography>}
        {user.roles.inventor ?
            <Typography variant="caption" paragraph={true}>
                As you complete each module your homework will appear here as a downloadable PDF.<br/>
                You may also upload additional documents / images that you would like to share with investors.
            </Typography> : null}
        <div className="field">
            <FiltersAccordion
                filters={filters}
                form={FileFilters}
                open={showFilters}
                onOpen={() => setShowFilters(true)}
                onClose={() => setShowFilters(false)}
            />
        </div>
        <Cards
            items={filtered}
            paging={filters.paging}
            dimensions={dimensions}
            renderer={(file) =>
                <FileCard
                    key={file.id}
                    document={file}
                    filters={filters}
                >
                    <IconButton color="secondary" title="Delete" className="delete"
                                disabled={strings.isNotBlank(file.templateId) || progressUtils.hasDownloaded(progress, file.id)}
                                onClick={() => {
                                    setFile(file);
                                    setShowDelete(true);
                                }}>
                        <DeleteIcon/>
                    </IconButton>
                    {downloading && downloading.id === file.id ?
                        <CircularProgress size="24px" className="downloading"/> :
                        <IconButton
                            color="primary" title="Download"
                            onClick={() => downloadFile(file)}
                        >
                            <DownloadIcon/>
                        </IconButton>}
                </FileCard>}
        />
        <FloatingAddButton
            title="Add file"
            position={dimensions.portrait ? "higher" : undefined}
            center={false}
            disabled={user.roles.admin && !userId}
            onClick={() => {
                setShowAdd(true);
            }}
        />
        {showAdd ?
            <AddFileDialog
                snackbar={snackbar}
                userId={userId}
                onCancel={() => {
                    setShowAdd(false);
                }}
                onSave={(saved) => {
                    setShowAdd(false);
                    onSave(saved);
                }}
            /> : null}
        {showDelete && file ?
            <ConfirmDeleteDialog
                snackbar={snackbar}
                title={file.fileName}
                onCancel={() => {
                    setShowDelete(false);
                    setFile(null);
                }}
                onDelete={(fileId) => {
                    setShowDelete(false);
                    setFile(null);
                    onDelete(fileId);
                }}
                deleter={(success, failure) => documentService.deleteFile(file.id, success, failure)}
            /> : null}
    </div>
}

export default withRouter(FilesPage);
